// ─────────────────────────────────────────────────────────────────────
// PADEL24 RESULT HELPERS
//
// Pure result/label helpers for the Padel24 tournament format, lifted
// verbatim out of legacy/App.jsx (PR-AS) as part of decommissioning the
// monolith. Previously threaded into the views as props; now imported from
// the engine layer.
//
// Engine binding note: in the former monolith applyPadel24Result called
// `Padel24Engine.elo.getPlayerKMultiplier`. In src/engine/platformEngine.ts
// that member is a STATIC reference to the `./elo` export
// `getPlayerKMultiplier` (not a getter), so importing it directly is
// behaviour-identical. The standings/placeholder/bonus primitives
// (applyMatchResult, rankGroups, resolvePlaceholders, awardPhaseBonuses)
// come from `./padel24`, and applyEloUpdate from `./elo` — the same bindings
// the monolith imported.
// ─────────────────────────────────────────────────────────────────────
import { applyMatchResult, rankGroups, resolvePlaceholders, awardPhaseBonuses } from './padel24.js';
import { applyEloUpdate, getPlayerKMultiplier } from './elo.js';
import { getTeamPlayerIds } from './padel24.js';
import { calcAmericanoStandings } from './americano.js';
import { seriespillStandingsByDivision } from './seriespill.js';
import { computeKMClassStandings } from './kmResults.js';
import { resolveEffects, type EngineEffects } from './effects.js';

export function applyPadel24Result(
  s: any,
  matchId: any,
  score1: any,
  score2: any,
  source: any,
  effects?: EngineEffects,
) {
  // One clock read for the whole application (#203). This previously read the
  // clock five separate times — twice for the recorded newbie multipliers, once
  // for `completedAt`, once for `playedAt`, and once more inside applyEloUpdate
  // for `lastMatchAt` — so a result could land with timestamps a millisecond
  // apart. Omitting `effects` still reads Date.now(); it now reads it once.
  const { now, settings } = resolveEffects(effects);
  const match = s.matches.find((m: any) => m.id === matchId);
  if (!match) return s;
  // Safety net: never record a result in preview mode.
  if (s.status === 'ready') return s;

  // Finals lock: even though the finals are resolved per group (so the names
  // are shown early), they must NOT be recordable until all groups have
  // finished playing the last phase. Return unchanged state on any attempt.
  if (match.isFinal && s.status !== 'finals') {
    return s;
  }

  // ──────────────────────────────────────────────────────────
  // EDITING: if the match was ALREADY completed, reverse the
  // old effect (ELO + matchesPlayed + standings table) before the new
  // result is applied. Without this the old result lingers
  // and double-counts (e.g. when correcting a loss → win).
  // ──────────────────────────────────────────────────────────
  // A FINAL IS NOT A GROUP MATCH.
  //
  // `applyMatchResult` counts points, games, wins and matchesPlayed, and knows
  // nothing about what kind of match it was — so a final landed in the GROUP
  // standings and the «Sluttoppstilling» table read «3/3» where the group had
  // played two. The final decides a PLACE (Finale 1 → 1st/2nd), which
  // `calcPlayerPositionInTournament` reads from the match itself; it was never
  // meant to move anybody up the group table as well.
  //
  // ELO IS UNAFFECTED, deliberately. `applyEloUpdate` and the player's own
  // career `matchesPlayed` are separate from this, and a final is a real match
  // that should rate like one.
  //
  // SYMMETRIC WITH THE REVERSAL BELOW, and that is the whole care of it: an
  // edit reverses what a result added. Skipping only the ADD would let a
  // corrected final subtract points it never contributed, walking the group
  // table down by one edit at a time — a slower, quieter version of the same
  // defect.
  const countsInGroupStandings = !match.isFinal;

  let basePlayers = s.players;
  let baseStandings = s.standings;
  if (match.completed) {
    const rT1 = [match.team1.p1, match.team1.p2]
      .filter((sl: any) => sl.kind === 'concrete')
      .map((sl: any) => sl.playerId);
    const rT2 = [match.team2.p1, match.team2.p2]
      .filter((sl: any) => sl.kind === 'concrete')
      .map((sl: any) => sl.playerId);
    basePlayers = s.players.map((p: any) => {
      if (rT1.includes(p.id))
        return {
          ...p,
          elo: p.elo - (match.eloChange1 || 0),
          matchesPlayed: Math.max(0, (p.matchesPlayed || 0) - 1),
        };
      if (rT2.includes(p.id))
        return {
          ...p,
          elo: p.elo - (match.eloChange2 || 0),
          matchesPlayed: Math.max(0, (p.matchesPlayed || 0) - 1),
        };
      return p;
    });
    const oldS1 = match.score1 || 0,
      oldS2 = match.score2 || 0;
    const revSlots = [
      ...[match.team1.p1, match.team1.p2].map((sl: any) => ({ slot: sl, won: oldS1, lost: oldS2 })),
      ...[match.team2.p1, match.team2.p2].map((sl: any) => ({ slot: sl, won: oldS2, lost: oldS1 })),
    ];
    const rs = { ...s.standings };
    // Only what was ADDED is taken back — see `countsInGroupStandings` above.
    if (countsInGroupStandings) {
      revSlots.forEach(({ slot, won, lost }: any) => {
        if (slot.kind !== 'concrete') return;
        const cur = rs[slot.playerId];
        if (!cur) return;
        rs[slot.playerId] = {
          ...cur,
          points: cur.points - won,
          gamesWon: cur.gamesWon - won,
          gamesLost: cur.gamesLost - lost,
          matchesPlayed: Math.max(0, cur.matchesPlayed - 1),
          wins: Math.max(0, cur.wins - (won > lost ? 1 : 0)),
          draws: Math.max(0, (cur.draws || 0) - (won === lost ? 1 : 0)),
        };
      });
    }
    baseStandings = rs;
  }

  const newStandings = countsInGroupStandings
    ? applyMatchResult(baseStandings, match, score1, score2)
    : baseStandings;
  // ELO weight: use the tournament's configured kMultiplier (default 1.0).
  // For old tournaments without the field we fall back to 1.0.
  const kMul = s.config?.kMultiplier ?? 1.0;
  const newPlayers = applyEloUpdate(basePlayers, match, score1, score2, kMul, now, settings);

  // ELO change (based on the actual diff so the MOV multiplier is included)
  let t1Change = 0,
    t2Change = 0;
  let teamElo1Pre = null,
    teamElo2Pre = null;
  const playerEloPre: any = {}; // { playerId: elo value before the match }
  let t1NewbieMul = 1,
    t2NewbieMul = 1; // Newbie boost per team (1.0 normal, 2.0 beginner)
  const t1 = [match.team1.p1, match.team1.p2].filter((sl: any) => sl.kind === 'concrete');
  const t2 = [match.team2.p1, match.team2.p2].filter((sl: any) => sl.kind === 'concrete');
  if (t1.length === 2 && t2.length === 2) {
    const oldT1a = basePlayers.find((p: any) => p.id === t1[0].playerId);
    const oldT1b = basePlayers.find((p: any) => p.id === t1[1].playerId);
    const oldT2a = basePlayers.find((p: any) => p.id === t2[0].playerId);
    const oldT2b = basePlayers.find((p: any) => p.id === t2[1].playerId);
    const newT1 = newPlayers.find((p: any) => p.id === t1[0].playerId);
    const newT2 = newPlayers.find((p: any) => p.id === t2[0].playerId);
    if (oldT1a && oldT1b && oldT2a && oldT2b && newT1 && newT2) {
      t1Change = newT1.elo - oldT1a.elo;
      t2Change = newT2.elo - oldT2a.elo;
      teamElo1Pre = Math.round((oldT1a.elo + oldT1b.elo) / 2);
      teamElo2Pre = Math.round((oldT2a.elo + oldT2b.elo) / 2);
      // Store the individual pre-match ELO for each player
      playerEloPre[oldT1a.id] = oldT1a.elo;
      playerEloPre[oldT1b.id] = oldT1b.elo;
      playerEloPre[oldT2a.id] = oldT2a.elo;
      playerEloPre[oldT2b.id] = oldT2b.elo;
      // Compute the newbie multiplier based on the STATE BEFORE the match
      // (so that we show what was actually applied to exactly this match).
      // Pass the tournament's kMultiplier so the threshold scales correctly
      // for e.g. americano (50% kMul), which requires more matches.
      t1NewbieMul = Math.max(
        getPlayerKMultiplier(oldT1a, now, kMul, settings),
        getPlayerKMultiplier(oldT1b, now, kMul, settings),
      );
      t2NewbieMul = Math.max(
        getPlayerKMultiplier(oldT2a, now, kMul, settings),
        getPlayerKMultiplier(oldT2b, now, kMul, settings),
      );
    }
  }

  const eloLog =
    t1Change !== 0 || t2Change !== 0
      ? ` · ELO ${t1Change > 0 ? '+' : ''}${t1Change} / ${t2Change > 0 ? '+' : ''}${t2Change}`
      : '';

  const newMatches = s.matches.map((m: any) =>
    m.id === matchId
      ? {
          ...m,
          score1,
          score2,
          completed: true,
          eloChange1: t1Change,
          eloChange2: t2Change,
          teamElo1Pre,
          teamElo2Pre,
          playerEloPre,
          t1NewbieMul,
          t2NewbieMul,
          completedAt: m.completedAt || now,
          playedAt: m.playedAt || m.completedAt || now,
        }
      : m,
  );

  // ──────────────────────────────────────────────────────────
  // PER-GROUP FINALS RESOLVING (last phase)
  //
  // If this was a match in the last phase: check whether the GROUP the match
  // belongs to now has all of its last-phase matches finished. If so,
  // compute the ranking for that group only and resolve the finals that
  // are tied to that group.
  //
  // This lets lower divisions' finals start in parallel while
  // higher divisions are still playing their last-phase matches.
  // ──────────────────────────────────────────────────────────
  let resolvedMatches = newMatches;
  let newRankings = s.groupRankings || {};
  let newStatus = s.status;
  const extraLog: any[] = [];
  let bonusStandings = newStandings;
  let newHistory = s.groupHistory;

  const isLastPhaseMatch = match.phase === s.phasesTotal && !match.isFinal;
  if (isLastPhaseMatch) {
    const matchGroup = match.primaryGroup || match.groupContext;

    if (matchGroup) {
      // Check whether all of this group's last-phase matches are finished now
      const groupMatches = newMatches.filter(
        (m: any) =>
          m.phase === s.phasesTotal &&
          !m.isFinal &&
          (m.primaryGroup === matchGroup || m.groupContext === matchGroup),
      );
      const groupDone = groupMatches.length > 0 && groupMatches.every((m: any) => m.completed);

      // Already ranked? (should not be able to happen, but check just to be safe)
      const phaseRanks = newRankings[s.phasesTotal] || {};
      const alreadyRanked = !!phaseRanks[matchGroup];

      if (groupDone && !alreadyRanked) {
        // Rank ALL groups, but pick only the value for this group.
        // rankGroups uses standings (the sum of all finished matches), so
        // partial state for other groups is expected and harmless.
        const allRanking = rankGroups(s.groups, newStandings, newMatches, s.config?.rankingOrder);
        const groupRanking = allRanking[matchGroup];

        if (groupRanking) {
          newRankings = {
            ...newRankings,
            [s.phasesTotal]: {
              ...phaseRanks,
              [matchGroup]: groupRanking,
            },
          };
          // Resolve placeholders → the group's finals get concrete players
          resolvedMatches = resolvePlaceholders(newMatches, newRankings);
          extraLog.push({
            code: 'groupDoneFinalsReady',
            vars: { group: matchGroup, phase: s.phasesTotal },
          });

          // Check whether ALL groups are now finished
          const allCompleted = s.groups.every((g: any) => newRankings[s.phasesTotal]?.[g.letter]);

          if (allCompleted && s.status !== 'finals') {
            // The whole tournament is ready for finals status.
            // Lucky Looser is handled by the slot mapping in
            // generateFinalsStructure — the finals slots already point
            // to the right (group, placement), so a normal resolve suffices.
            bonusStandings = awardPhaseBonuses(newStandings, s.groups);
            resolvedMatches = resolvePlaceholders(resolvedMatches, newRankings);
            newStatus = 'finals';
            newHistory = {
              ...(s.groupHistory || {}),
              [s.phasesTotal]: {
                phase: s.phasesTotal,
                groups: s.groups.map((g: any) => ({ letter: g.letter, players: [...g.players] })),
                standings: bonusStandings,
              },
            };
            extraLog.push({ code: 'allGroupsDoneBonus' });
          }
        }
      }
    }
  }

  // Resolve label for log
  const t1Names = teamLabel(match.team1, s.players);
  const t2Names = teamLabel(match.team2, s.players);
  // WHICH SENTENCE, not which language. The source is part of what happened —
  // a Banepad tablet recorded it, or corrected it — so it picks the event code
  // rather than being glued on as a prefix. A `[Banepad] ` tag prepended to an
  // already-translated sentence is exactly the persisted-prose problem #519
  // removed, one layer up.
  const resultCode =
    source === 'banepad'
      ? 'resultBanepad'
      : source === 'banepad-edit'
        ? 'resultBanepadEdit'
        : match.isFinal
          ? 'finalResult'
          : 'result';

  const logTime = new Date(now).toISOString();
  const allLogs = [
    ...s.log,
    {
      time: logTime,
      code: resultCode,
      vars: { team1: t1Names, score1, score2, team2: t2Names, elo: eloLog },
    },
    ...extraLog.map((e: any) => ({ time: logTime, ...e })),
  ];

  return {
    ...s,
    matches: resolvedMatches,
    standings: bonusStandings,
    players: newPlayers,
    groupRankings: newRankings,
    groupHistory: newHistory,
    status: newStatus,
    log: allLogs,
  };
}

// Helper: return a nice display name for a team (even with placeholders)
export function teamLabel(team: any, players: any) {
  const slotName = (slot: any) => {
    if (slot.kind === 'concrete') {
      const p = players.find((pp: any) => pp.id === slot.playerId);
      return p ? p.name.split(' ')[0] : '?';
    }
    return slot.label;
  };
  return `${slotName(team.p1)}/${slotName(team.p2)}`;
}

// Compute the next available tournament displayId based on all existing ones.
// Used when creating new tournaments.
export function nextDisplayTournamentId(allTournaments: any) {
  const nums = [];
  for (const t of allTournaments) {
    if (!t?.displayId) continue;
    const m = t.displayId.match(/^T(\d+)$/);
    if (m) nums.push(parseInt(m[1], 10));
  }
  const max = nums.length ? Math.max(...nums) : 0;
  return `T${max + 1}`;
}

// calcPlayerPositionInTournament — a player's finishing position in a finished
// tournament (seriespill/KM/americano-family/padel24). Deferred from PR-AS to
// PR-AT because it calls computeKMClassStandings (a KM helper, now in ./kmResults).
export function calcPlayerPositionInTournament(playerId: any, t: any) {
  if (!t || !t.matches) return null;

  // Seriespill (league play): position = the pair's ranking in its division for the
  // current season. Shown even while the league is ongoing — the league gives one position per season.
  if (t.type === 'seriespill') {
    const pair = (t.pairs || []).find((p) => p.p1Id === playerId || p.p2Id === playerId);
    if (!pair) return null;
    const stByDiv = seriespillStandingsByDivision(t);
    const standings = stByDiv[pair.divisionId] || [];
    const entry = standings.find((s) => s.pair?.id === pair.id);
    if (!entry || entry.played === 0) return null;
    let className = 'Seriespill';
    (t.config?.classes || []).forEach((c) =>
      (c.divisions || []).forEach((d) => {
        if (d.id === pair.divisionId) className = `${c.name} ${d.name}`;
      }),
    );
    return {
      position: entry.pos,
      total: standings.length,
      type: 'seriespill',
      format: 'seriespill',
      className,
      season: t.currentSeason,
    };
  }

  const isDone = t.status === 'done' || t.isArchived;
  if (!isDone) return null; // Ongoing tournaments do not count

  // Klubbmesterskap (club championship): position = the player's ranking in their class (results)
  if (t.type === 'klubbmesterskap') {
    const pair = (t.pairs || []).find((p) => p.p1Id === playerId || p.p2Id === playerId);
    if (!pair) return null;
    const standings = computeKMClassStandings(t, pair.divisionId);
    const entry = standings.find((s) => s.pairId === pair.id);
    if (!entry || entry.played === 0) return null;
    let className = 'Klubbmesterskap';
    (t.config?.classes || []).forEach((c) =>
      (c.divisions || []).forEach((d) => {
        if (d.id === pair.divisionId) className = `${c.name} ${d.name}`;
      }),
    );
    return {
      position: entry.pos,
      total: standings.length,
      type: 'km',
      format: 'klubbmesterskap',
      className,
    };
  }

  // Americano, Mexicano, Mixicano, Team Mexicano and Mix Americano: can be standings mode or finals mode
  if (
    t.type === 'americano' ||
    t.type === 'mexicano' ||
    t.type === 'mixicano' ||
    t.type === 'team_mexicano' ||
    t.type === 'mix_americano' ||
    t.type === 'club_americano' ||
    t.type === 'club_team_americano' ||
    t.type === 'club_mexicano'
  ) {
    const winnerSource = t.config?.winnerSource || 'finals';
    const formatLabel =
      t.type === 'team_mexicano'
        ? 'team_mexicano'
        : t.type === 'mix_americano'
          ? 'mix_americano'
          : t.type === 'club_americano'
            ? 'club_americano'
            : t.type === 'club_team_americano'
              ? 'club_team_americano'
              : t.type === 'club_mexicano'
                ? 'club_mexicano'
                : t.type === 'mixicano'
                  ? 'mixicano'
                  : t.type === 'mexicano'
                    ? 'mexicano'
                    : 'americano';

    if (winnerSource === 'standings') {
      // Use the leaderboard ranking
      try {
        const standings = calcAmericanoStandings(t);
        const entry = standings.find((s) => s.playerId === playerId);
        if (!entry || entry.injured) return null;
        return {
          position: entry.rank,
          total: standings.filter((s) => !s.injured).length,
          type: 'standings',
          format: formatLabel,
        };
      } catch {
        return null;
      }
    }

    // Americano/Mexicano finals — winner = 2*(fn-1)+1, loser = +1
    const finals = (t.matches || [])
      .filter((m) => m.isFinal && m.completed)
      .sort((a, b) => (a.finalNumber || 0) - (b.finalNumber || 0));

    for (const f of finals) {
      const [t1p1, t1p2] = getTeamPlayerIds(f.team1);
      const [t2p1, t2p2] = getTeamPlayerIds(f.team2);
      const inTeam1 = t1p1 === playerId || t1p2 === playerId;
      const inTeam2 = t2p1 === playerId || t2p2 === playerId;
      if (!inTeam1 && !inTeam2) continue;
      const t1Won = f.score1 > f.score2;
      const won = (inTeam1 && t1Won) || (inTeam2 && !t1Won);
      const winnerPos = (f.finalNumber - 1) * 2 + 1;
      return {
        position: won ? winnerPos : winnerPos + 1,
        total: t.players?.length || 0,
        type: 'finals',
        format: formatLabel,
      };
    }
    return null;
  }

  // Padel24 — winner = (fn-1)*2+1, loser = +1 (together with partner)
  // Final 1 → 1st/2nd place. Final 2 → 3rd/4th place. And so on.
  const finals = (t.matches || [])
    .filter((m) => m.isFinal && m.completed)
    .sort((a, b) => (a.finalNumber || 0) - (b.finalNumber || 0));

  for (const f of finals) {
    const [t1p1, t1p2] = getTeamPlayerIds(f.team1);
    const [t2p1, t2p2] = getTeamPlayerIds(f.team2);
    const inTeam1 = t1p1 === playerId || t1p2 === playerId;
    const inTeam2 = t2p1 === playerId || t2p2 === playerId;
    if (!inTeam1 && !inTeam2) continue;
    const t1Won = f.score1 > f.score2;
    const won = (inTeam1 && t1Won) || (inTeam2 && !t1Won);
    const basePos = (f.finalNumber - 1) * 2 + 1;
    return {
      position: won ? basePos : basePos + 1,
      total: t.players?.length || 0,
      type: 'finals',
      format: 'padel24',
    };
  }
  return null;
}
